import React, { useEffect, useState } from 'react';
import axios from "axios";
import {
  Card, CardBody, Col, Container, Input, Label, Row, Button, Form,
  FormFeedback, Alert, Spinner
} from 'reactstrap';
import { Link, useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import logoLight from "../../assets/images/Logo_V2-2_white.png";


interface VerifyData {
  valid: boolean;
  email_mask?: string;
  fullname?: string;
  expires_at?: string;
}

const validationSchema = Yup.object({
  password: Yup.string()
    .min(8, "Le mot de passe doit contenir au moins 8 caractères")
    .required("Veuillez saisir votre nouveau mot de passe"),
  password_confirm: Yup.string()
    .oneOf([Yup.ref("password")], "Les mots de passe ne correspondent pas")
    .required("Veuillez confirmer votre mot de passe"),
});


const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [verifying, setVerifying] = useState(true);
  const [verifyOk, setVerifyOk] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyData, setVerifyData] = useState<VerifyData | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);

  const [passwordShow, setPasswordShow] = useState(false);
  const [passwordShow2, setPasswordShow2] = useState(false);

  document.title = "Nouveau mot de passe | IP Trend";

  useEffect(() => {
    (async () => {
      if (!token) {
        setVerifying(false);
        setVerifyError("Lien de réinitialisation invalide.");
        return;
      }
      try {
        const res: any = await axios.post(
          "/apiV2/",
          { action: "resetpassword", subaction: "verify", token },
          { headers: { "Content-Type": "application/json" } }
        );
        const payload = res?.data ?? res;
        if (payload?.success) {
          setVerifyOk(true);
          setVerifyData(payload?.data || null);
        } else {
          setVerifyError(payload?.message || "Lien invalide.");
        }
      } catch (e: any) {
        const msg =
          typeof e === "string"
            ? e
            : e?.response?.data?.message || e?.message || "Lien invalide ou expiré.";
        setVerifyError(String(msg));
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  const validation = useFormik({
    initialValues: { password: "", password_confirm: "" },
    validationSchema,
    onSubmit: async (values) => {
      if (!token) return;
      setSubmitting(true);
      setSubmitError(null);
      try {
        const res: any = await axios.post(
          "/apiV2/",
          {
            action: "resetpassword",
            subaction: "submit",
            token,
            password: values.password,
            password_confirm: values.password_confirm,
          },
          { headers: { "Content-Type": "application/json" } }
        );
        const payload = res?.data ?? res;
        if (payload?.success) {
          setSubmitOk(true);
          setTimeout(() => navigate("/login"), 3000);
        } else {
          setSubmitError(payload?.message || "Erreur lors de la mise à jour.");
        }
      } catch (e: any) {
        const msg =
          typeof e === "string"
            ? e
            : e?.response?.data?.message || e?.message || "Erreur lors de la mise à jour.";
        setSubmitError(String(msg));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={logoLight} alt="IP Trend" height="80" />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">Votre espace client IP Trend</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Nouveau mot de passe</h5>
                      <p className="text-muted">
                        Choisissez un nouveau mot de passe pour votre compte
                      </p>
                    </div>

                    {verifying && (
                      <div className="text-center py-4">
                        <Spinner style={{ color: "#0066A1" }} />
                        <p className="text-muted mt-2">Vérification du lien…</p>
                      </div>
                    )}

                    {!verifying && verifyError && (
                      <>
                        <Alert color="danger" className="mx-2">
                          <i className="ri-error-warning-line me-2"></i>
                          {verifyError}
                        </Alert>
                        <div className="text-center mt-3">
                          <Link to="/forgot-password" className="btn btn-outline-primary">
                            Demander un nouveau lien
                          </Link>
                        </div>
                      </>
                    )}

                    {!verifying && verifyOk && !submitOk && (
                      <>
                        {verifyData?.fullname && (
                          <Alert color="info" className="alert-borderless mx-2 fs-13">
                            <i className="ri-user-line me-1"></i>
                            Compte : <strong>{verifyData.fullname}</strong>
                            {verifyData.email_mask && (
                              <span className="text-muted ms-2">({verifyData.email_mask})</span>
                            )}
                          </Alert>
                        )}

                        {submitError && (
                          <Alert color="danger" className="mx-2">
                            <i className="ri-error-warning-line me-2"></i>
                            {submitError}
                          </Alert>
                        )}

                        <div className="p-2">
                          <Form
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                              return false;
                            }}
                          >
                            <div className="mb-3">
                              <Label htmlFor="password" className="form-label">
                                Nouveau mot de passe
                              </Label>
                              <div className="position-relative auth-pass-inputgroup mb-3">
                                <Input
                                  name="password"
                                  type={passwordShow ? "text" : "password"}
                                  className="form-control pe-5"
                                  placeholder="Au moins 8 caractères"
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  value={validation.values.password}
                                  invalid={!!(validation.touched.password && validation.errors.password)}
                                />
                                {validation.touched.password && validation.errors.password && (
                                  <FormFeedback type="invalid">
                                    {validation.errors.password as string}
                                  </FormFeedback>
                                )}
                                <button
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  onClick={() => setPasswordShow(!passwordShow)}
                                >
                                  <i className="ri-eye-fill align-middle"></i>
                                </button>
                              </div>
                            </div>

                            <div className="mb-3">
                              <Label htmlFor="password_confirm" className="form-label">
                                Confirmer le mot de passe
                              </Label>
                              <div className="position-relative auth-pass-inputgroup mb-3">
                                <Input
                                  name="password_confirm"
                                  type={passwordShow2 ? "text" : "password"}
                                  className="form-control pe-5"
                                  placeholder="Ressaisissez le mot de passe"
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  value={validation.values.password_confirm}
                                  invalid={!!(validation.touched.password_confirm && validation.errors.password_confirm)}
                                />
                                {validation.touched.password_confirm && validation.errors.password_confirm && (
                                  <FormFeedback type="invalid">
                                    {validation.errors.password_confirm as string}
                                  </FormFeedback>
                                )}
                                <button
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  onClick={() => setPasswordShow2(!passwordShow2)}
                                >
                                  <i className="ri-eye-fill align-middle"></i>
                                </button>
                              </div>
                            </div>

                            <div className="mt-4">
                              <Button
                                color="success"
                                className="btn btn-success w-100"
                                type="submit"
                                disabled={submitting}
                              >
                                {submitting && <Spinner size="sm" className="me-2"> Chargement... </Spinner>}
                                <i className="ri-lock-password-line me-1"></i>
                                Valider le nouveau mot de passe
                              </Button>
                            </div>
                          </Form>
                        </div>
                      </>
                    )}

                    {submitOk && (
                      <>
                        <Alert color="success" className="mx-2">
                          <i className="ri-checkbox-circle-line me-2"></i>
                          Votre mot de passe a été mis à jour avec succès.
                          <br />
                          <small>Redirection vers la page de connexion…</small>
                        </Alert>
                        <div className="text-center mt-3">
                          <Link to="/login" className="btn btn-primary">
                            Se connecter maintenant
                          </Link>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                <div className="mt-4 text-center">
                  <p className="mb-0 text-white-50">
                    <Link to="/login" className="fw-semibold text-white text-decoration-underline">
                      Retour à la connexion
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default ResetPassword;
